import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

/**
 * Demo mode — operator-facing toggle that swaps every data hook from
 * the live engine API to bundled JSON fixtures. Used for sales demos
 * (walk a prospect through the product without showing real customer
 * data) and net-new-tenant onboarding (show what the product looks
 * like before any scenarios have synced).
 *
 * Persistence: `localStorage['demoMode']` carries the operator's
 * preference across reloads. The legacy SPA also wrote a cookie for
 * SSR compat — the new SPA is client-only, so we skip the cookie.
 *
 * Reload-on-toggle: the legacy SPA reloaded the page after flipping
 * the toggle so in-memory caches drained. The new SPA uses TanStack
 * Query — `useDemoOrLive` watches `isDemoMode` and re-renders all
 * consumers without a reload. No window.location.reload needed.
 */

const STORAGE_KEY = 'demoMode';

interface DemoModeContextValue {
  isDemoMode: boolean;
  toggleDemoMode: () => void;
  enableDemoMode: () => void;
  disableDemoMode: () => void;
}

const DemoModeContext = createContext<DemoModeContextValue | null>(null);

function readInitial(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    // Storage access can throw in sandboxed iframes or when the
    // operator's browser has cookies/storage disabled. Treat as off.
    return false;
  }
}

export function DemoModeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [isDemoMode, setIsDemoMode] = useState<boolean>(() => readInitial());

  // Cross-tab sync: if another tab flips the toggle, mirror the state
  // here so the operator doesn't see stale data on the other tab.
  useEffect(() => {
    function onStorage(event: StorageEvent): void {
      if (event.key === STORAGE_KEY) {
        setIsDemoMode(event.newValue === 'true');
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const persist = useCallback((next: boolean): void => {
    setIsDemoMode(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next.toString());
    } catch {
      /* same sandbox case as above — silently ignore */
    }
  }, []);

  const value: DemoModeContextValue = {
    isDemoMode,
    toggleDemoMode: () => persist(!isDemoMode),
    enableDemoMode: () => {
      if (!isDemoMode) persist(true);
    },
    disableDemoMode: () => {
      if (isDemoMode) persist(false);
    },
  };

  return <DemoModeContext.Provider value={value}>{children}</DemoModeContext.Provider>;
}

export function useDemoMode(): DemoModeContextValue {
  const ctx = useContext(DemoModeContext);
  if (!ctx) {
    throw new Error('useDemoMode must be used within <DemoModeProvider>');
  }
  return ctx;
}

/**
 * Non-hook accessor for the demo flag — read directly from
 * localStorage. Use this in stores / mutation handlers / anywhere
 * a React hook isn't appropriate. Cheap (single `getItem` call) but
 * NOT reactive — it returns a snapshot, not a live binding.
 */
export function getDemoMode(): boolean {
  return readInitial();
}
